import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@attendance/ui/utils';

/** Two digits plus an overflow marker — a badge is a signal, not a readout. */
export function formatUnread(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/**
 * WHICH GROUND THE PILL SITS ON, and what that colour is allowed to claim.
 *
 * `danger` is the original and the default: the bell's unread count, and the
 * same pill echoed on the device-approvals page beside its heading. It is red
 * because it is about the reader — mail addressed to them, sitting unopened.
 *
 * `nav` is the rail's own ink wash, and it exists because the rail now carries
 * THREE of these pills where it carried one. Three permanent red discs on a
 * console that always has a correction or two outstanding is the alarm-fatigue
 * failure the dashboard's "Needs a decision" card is explicitly written to
 * avoid — it hides its rows at zero for exactly that reason. A queue with rows
 * in it is work, not a fault, and the rail says so in the rail's own palette:
 * `--nav-on-surface` at /15 behind full-opacity ink, which is the handoff's own
 * treatment for the device-approvals pill and measures far above the 4.5:1 a
 * figure needs, in both themes, on the ink that does not invert.
 *
 * `navBrand` and `warning` restore the handoff's own per-queue hues — Correction
 * requests in the brand, Leave approvals in `--warning` — on the product
 * owner's direction, matching `Hadir Dashboard.dc.html` exactly (`#5323e6`
 * and `#B4530A`, which is what `--primary` and `--warning` already resolve
 * to).
 *
 * `navBrand` IS THE BRAND, IN THE RAIL'S FORM, and the distinction is the
 * difference between a pill you can see and one you cannot. It was
 * `bg-primary text-primary-foreground`, and `--primary` at #5323e6 measures
 * 1.73:1 against the ink rail — the very reason `--nav-active` exists (index.css:
 * "far too dark against #1B1A21 to mark anything"). So the pill on the rail
 * takes the rail's own brand: `--nav-active` at 5.90:1 / 6.40:1 on the rail,
 * with `--nav-surface` — the rail itself — as its ink, which is the same 5.90:1
 * pair read the other way round and far above the 4.5:1 a figure owes. The tone
 * is named for where it belongs so nothing off the rail reaches for it. An earlier pass here argued for one neutral tone across all three
 * queues, on the theory that colouring one amber asserts a severity ranking
 * between equally-serious queues; the owner's call is that the hues are for
 * TELLING THE QUEUES APART at a glance rather than ranking them, which is
 * also what the handoff itself uses them for — three colours as three
 * identities, not a ladder. `deviceApprovals` keeps the neutral `nav` tone,
 * matching the handoff's own OWN treatment for that one pill.
 */
export type BadgeTone = 'danger' | 'nav' | 'navBrand' | 'warning' | 'quiet';

const TONE: Record<BadgeTone, string> = {
  danger: 'bg-danger text-danger-foreground',
  nav: 'bg-nav-on-surface/15 text-nav-on-surface',
  navBrand: 'bg-nav-active text-nav-surface',
  warning: 'bg-warning text-warning-foreground',
  // A count that is NOT an alarm, on the light page — the Tasks board's open
  // pile beside its red overdue and amber review. Ink at /10 holds on both the
  // segmented track and its raised selected segment, in either theme.
  quiet: 'bg-foreground/10 text-foreground',
};

/**
 * The count pill. ONE component for the desktop bell, the mobile "Alerts" tab
 * and every counted queue on the rail, so they cannot disagree about the
 * number, the 99+ cut-off or the shape.
 *
 * 12px is the app's type floor and a count nobody can read is not a
 * notification, so the pill grows to 18px rather than the digit shrinking.
 *
 * SQUARED, not round. It was `rounded-full`, which is right for a one-digit disc
 * and wrong for "99+" — a capsule that long stops reading as a badge and starts
 * reading as a chip. `rounded-xs` (8px) is the system's badge corner and is what
 * the handoff draws in both places this pill appears, the rail row and the bell,
 * so the two stay one object. `--radius-full` survives in the ramp for the live
 * dot, which is genuinely a circle.
 *
 * The digit is `aria-hidden` with an sr-only sentence beside it: "5" read out
 * on its own is not a number of anything. `label` names what is being counted
 * when it is not unread mail — "12 waiting for a decision" — because the rail's
 * pills sit inside a link whose accessible name is everything in it.
 */
export function UnreadBadge({
  count,
  className,
  tone = 'danger',
  label,
}: {
  count: number;
  className?: string;
  tone?: BadgeTone;
  /** Screen-reader sentence. Defaults to the notifications wording. */
  label?: ReactNode;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        'inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-xs px-1 text-label font-bold leading-none tabular-nums',
        TONE[tone],
        className
      )}
    >
      <span aria-hidden>{formatUnread(count)}</span>
      <span className="sr-only">
        {label ?? t('notifications.unreadBadge', '{{count}} unread', { count })}
      </span>
    </span>
  );
}
